"use client";

import { useEffect, useRef } from "react";
import { ChartBar as BarChart3, Globe, MapPin, Target, ShoppingCart, Search, FileText, Bot } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
const rituals = [{
  icon: BarChart3,
  number: "01",
  title: "National SEO",
  description: "I build authority and search visibility across the UK, focusing on high-intent keywords to drive sustainable organic growth for your business.",
  meta: "Strategy - Implementation"
}, {
  icon: Globe,
  number: "02",
  title: "International SEO",
  description: "I scale your search presence across borders, managing multi-region technical architecture and content strategy for global market expansion.",
  meta: "Strategy - Implementation"
}, {
  icon: MapPin,
  number: "03",
  title: "Local SEO",
  description: "I optimise your Google Business Profile and local signals to ensure you dominate search results in your specific geographic service areas.",
  meta: "Strategy - Implementation"
}, {
  icon: Target,
  number: "04",
  title: "B2B SEO & Lead Gen",
  description: "I align your SEO strategy with your sales funnel, targeting decision-makers to generate high-quality B2B leads that convert into revenue.",
  meta: "Strategy - Implementation"
}, {
  icon: ShoppingCart,
  number: "05",
  title: "Ecommerce SEO",
  description: "I improve product visibility and site architecture to increase organic traffic and sales for your online store, focusing on conversion.",
  meta: "Strategy - Implementation"
}, {
  icon: Search,
  number: "06",
  title: "Technical SEO",
  description: "I audit and resolve complex crawl, indexation, and performance issues to ensure your site is fully accessible and favoured by search engines.",
  meta: "Strategy - Implementation"
}, {
  icon: FileText,
  number: "07",
  title: "SEO Audits",
  description: "I provide deep-dive, actionable audits that identify growth opportunities and technical blockers, giving you a clear roadmap for success.",
  meta: "Strategy - Implementation"
}, {
  icon: Bot,
  number: "08",
  title: "AI Search Optimisation",
  description: "I adapt your brand for the future of search, ensuring visibility across AI-powered search experiences and modern conversational interfaces.",
  meta: "Strategy - Implementation"
}];
export default function Services() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardsRef = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduced: "(prefers-reduced-motion: reduce)",
        normal: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduced: boolean;
          normal: boolean;
        };
        const validCards = cardsRef.current.filter((el): el is HTMLDivElement => el !== null);
        if (conditions.reduced) {
          gsap.set(validCards, {
            opacity: 1,
            y: 0
          });
          return;
        }
        gsap.from(validCards, {
          opacity: 0,
          y: 32,
          duration: 0.72,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            once: true
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="23037" ref={sectionRef} id="industrysectorsgrid" className="bg-background text-foreground py-32 md:py-48 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 mb-24 md:mb-32">
          <div className="md:col-span-2">
            <span className="font-sans uppercase text-xs font-medium text-muted-foreground" style={{
            letterSpacing: "0.16em"
          }}>
              MY SERVICES
            </span>
          </div>
          <div className="md:col-span-10">
            <h1 className="font-serif text-foreground" style={{
            fontWeight: 600,
            fontSize: "clamp(2.8125rem, 7.3vw, 6.75rem)",
            letterSpacing: "-0.028em",
            lineHeight: 0.96
          }}>
              {"Tailored SEO strategies for "}
              <span className="relative inline-block italic text-primary" style={{
              fontWeight: 400
            }}>
                sustainable growth
                <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true" style={{
                height: "0.35em"
              }}>
                  <path d="M2 8 Q 50 2, 100 6 T 198 5" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              {"."}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
          {rituals.map((ritual, i) => {
          const Icon = ritual.icon;
          return <div key={i} data-index={i} ref={el => {
            cardsRef.current[i] = el;
          }} className="group bg-card rounded-lg p-8 md:p-10 lg:p-12 flex flex-col min-h-[380px] transition-colors duration-300 hover:bg-muted border border-border">
                <div className="mb-8">
                  <Icon className="transition-transform duration-300 group-hover:translate-x-1 text-primary" style={{
                width: 32,
                height: 32,
                strokeWidth: 1.5
              }} aria-hidden="true" />
                </div>

                <div className="font-sans uppercase font-medium text-muted-foreground mb-4" style={{
              fontSize: "14px",
              letterSpacing: "0.16em"
            }}>
                  SERVICE - {ritual.number}
                </div>

                <h3 className="font-serif text-foreground mb-5" style={{
              fontWeight: 600,
              fontSize: "clamp(1.95rem, 2.7vw, 2.25rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.05
            }}>
                  {ritual.title}
                </h3>

                <p className="font-sans text-muted-foreground mb-10 flex-grow" style={{
              fontSize: "1.125rem",
              lineHeight: 1.6
            }}>
                  {ritual.description}
                </p>

                <div className="flex items-center justify-between gap-4 mt-auto">
                  <span className="font-sans text-muted-foreground" style={{
                fontSize: "clamp(0.8625rem, 0.98vw, 0.9375rem)",
                letterSpacing: "0.02em"
              }}>
                    {ritual.meta}
                  </span>
                  <Button variant="outline" className="rounded-md border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground px-6 py-2 h-auto font-sans font-medium transition-all duration-300" style={{
                letterSpacing: "0.01em"
              }}>
                    Learn More
                  </Button>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}